/**
 * チバリヨ - P6 システム設定画面
 *
 * セクション:
 *   A. 期間・組織 (systemName / companyName は readonly)
 *   B. アラート・閾値
 *   C. アバターカラーパレット (追加・編集・削除)
 *   D. 休業日カレンダー (holidays コレクション)
 *
 * 保存方針:
 *   - A/B/C は config/system 全体を上書き保存
 *   - D (holidays) は個別ドキュメントで CRUD
 *   - 一括「保存」ボタン + 警告ダイアログ
 *
 * 安全装置:
 *   - systemName / companyName / businessTypes は readonly
 *   - 使用中のアバターカラーは削除不可
 *   - 数値フィールドはバリデーション
 */

import {
  doc,
  collection,
  getDoc,
  setDoc,
  getDocs,
  query,
  orderBy,
  addDoc,
  deleteDoc,
  serverTimestamp
} from 'https://www.gstatic.com/firebasejs/10.13.0/firebase-firestore.js';

import { auth, db } from './firebase-init.js';

let configData = null;     // config/system のコピー
let holidays = [];         // holidays の現状
let activeMembers = [];    // 使用中カラー判定用

// =====================================================================
// 公開関数
// =====================================================================
export async function renderConfigPage(container) {
  container.innerHTML = `
    <div class="p6-header">
      <h1>システム設定</h1>
      <div class="p6-header-actions">
        <span id="p6-dirty-indicator" class="p6-dirty" style="display:none;">未保存の変更があります</span>
        <button id="p6-save-btn" class="btn btn-primary" disabled>変更を保存</button>
      </div>
    </div>
    <div id="p6-error" class="p6-error" style="display:none;"></div>
    <div id="p6-success" class="p6-success" style="display:none;"></div>
    <div id="p6-content" class="p6-content">
      <div class="p6-loading">読み込み中…</div>
    </div>
  `;

  document.getElementById('p6-save-btn').addEventListener('click', handleSave);

  await loadData();
  renderSections();
}

// =====================================================================
// データロード
// =====================================================================
async function loadData() {
  try {
    // config/system
    const cSnap = await getDoc(doc(db, 'config', 'system'));
    if (!cSnap.exists()) {
      showError('config/system が存在しません。Phase 0 のセットアップが必要です。');
      return;
    }
    configData = JSON.parse(JSON.stringify(cSnap.data()));

    // members (使用中カラー判定用)
    const mSnap = await getDocs(query(collection(db, 'members'), orderBy('order', 'asc')));
    activeMembers = mSnap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((m) => m.active !== false);

    // holidays
    const hSnap = await getDocs(query(collection(db, 'holidays'), orderBy('startDate', 'asc')));
    holidays = hSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    showError('読み込みに失敗しました: ' + err.message);
  }
}

// =====================================================================
// セクション描画
// =====================================================================
function renderSections() {
  if (!configData) return;

  const content = document.getElementById('p6-content');
  content.innerHTML = `
    ${renderSectionA()}
    ${renderSectionB()}
    ${renderSectionC()}
    ${renderSectionD()}
  `;

  bindSectionEvents();
}

// ----- セクション A: 期間・組織 -----
function renderSectionA() {
  return `
    <section class="p6-section">
      <h2 class="p6-section-title">期間・組織</h2>

      <div class="p6-grid-2">
        <div class="p6-field">
          <span class="p6-field-label">システム名</span>
          <div class="p6-readonly-value">${escapeHtml(configData.systemName || '')}</div>
          <span class="p6-field-hint">変更不可</span>
        </div>
        <div class="p6-field">
          <span class="p6-field-label">会社名</span>
          <div class="p6-readonly-value">${escapeHtml(configData.companyName || '')}</div>
          <span class="p6-field-hint">変更不可</span>
        </div>

        <label class="p6-field">
          <span class="p6-field-label">下半期 開始月</span>
          <input type="month" data-field="halfStart" value="${escapeAttr(configData.halfStart || '')}">
          <span class="p6-field-hint">例: 2025-12</span>
        </label>
        <label class="p6-field">
          <span class="p6-field-label">下半期 終了月</span>
          <input type="month" data-field="halfEnd" value="${escapeAttr(configData.halfEnd || '')}">
          <span class="p6-field-hint">例: 2026-05</span>
        </label>

        <label class="p6-field">
          <span class="p6-field-label">期(fiscalYear)</span>
          <input type="number" data-field="fiscalYear" value="${configData.fiscalYear ?? ''}" min="1" max="100">
        </label>
        <div></div>

        <label class="p6-field">
          <span class="p6-field-label">期 開始日</span>
          <input type="date" data-field="fiscalYearStart" value="${escapeAttr(configData.fiscalYearStart || '')}">
        </label>
        <label class="p6-field">
          <span class="p6-field-label">期 終了日</span>
          <input type="date" data-field="fiscalYearEnd" value="${escapeAttr(configData.fiscalYearEnd || '')}">
        </label>
      </div>

      ${Array.isArray(configData.businessTypes) ? `
        <div class="p6-field" style="margin-top:12px;">
          <span class="p6-field-label">事業区分(コード定義)</span>
          <div class="p6-chips">
            ${configData.businessTypes.map((b) => `<span class="p6-chip">${escapeHtml(b)}</span>`).join('')}
          </div>
          <span class="p6-field-hint">変更不可。アプリの内部処理で使用されます。</span>
        </div>
      ` : ''}
    </section>
  `;
}

// ----- セクション B: アラート・閾値 -----
function renderSectionB() {
  return `
    <section class="p6-section">
      <h2 class="p6-section-title">アラート・閾値</h2>

      <div class="p6-subgroup">
        <h3 class="p6-subgroup-title">通知アラート</h3>
        <div class="p6-grid-2">
          <label class="p6-field">
            <span class="p6-field-label">日報未入力アラート(日)</span>
            <input type="number" data-field="alertNoReportDays" value="${configData.alertNoReportDays ?? 3}" min="1" max="30">
            <span class="p6-field-hint">この日数以上、日報の入力がないメンバーをアラート</span>
          </label>
          <label class="p6-field">
            <span class="p6-field-label">訪問途絶アラート(日)</span>
            <input type="number" data-field="alertNoVisitDays" value="${configData.alertNoVisitDays ?? 30}" min="7" max="365">
            <span class="p6-field-hint">現在は使用されていません(訪問途絶日数は顧客マスタの個別設定で判定)</span>
          </label>
        </div>
      </div>

      <div class="p6-subgroup">
        <h3 class="p6-subgroup-title">ペース判定</h3>
        <div class="p6-grid-2">
          <label class="p6-field">
            <span class="p6-field-label">ペース危険アラート閾値(pt)</span>
            <input type="number" data-field="alertPacePtThreshold" value="${configData.alertPacePtThreshold ?? -15}" min="-50" max="0">
            <span class="p6-field-hint">ペース差がこの値以下でアラート</span>
          </label>
          <label class="p6-field">
            <span class="p6-field-label">月のこの日以降に判定(日)</span>
            <input type="number" data-field="alertPaceJudgeAfterDay" value="${configData.alertPaceJudgeAfterDay ?? 15}" min="1" max="28">
            <span class="p6-field-hint">月初は判定スキップ(初動でブレるため)</span>
          </label>
        </div>
      </div>

      <div class="p6-subgroup">
        <h3 class="p6-subgroup-title">色分け閾値(達成率ステータス)</h3>
        <div class="p6-grid-3">
          <label class="p6-field">
            <span class="p6-field-label">順調 ≥(pt)</span>
            <input type="number" data-field="paceThresholdGood" value="${configData.paceThresholdGood ?? 5}" min="0" max="30">
            <span class="p6-field-hint">緑(順調)の閾値</span>
          </label>
          <label class="p6-field">
            <span class="p6-field-label">要注意 ≤(pt)</span>
            <input type="number" data-field="paceThresholdWarning" value="${configData.paceThresholdWarning ?? -5}" min="-30" max="0">
            <span class="p6-field-hint">橙(要注意)の閾値</span>
          </label>
          <label class="p6-field">
            <span class="p6-field-label">遅延 ≤(pt)</span>
            <input type="number" data-field="paceThresholdDanger" value="${configData.paceThresholdDanger ?? -15}" min="-50" max="-5">
            <span class="p6-field-hint">赤(遅延)の閾値</span>
          </label>
        </div>
      </div>
    </section>
  `;
}

// ----- セクション C: アバターカラーパレット -----
function renderSectionC() {
  const colors = Array.isArray(configData.avatarColors) ? configData.avatarColors : [];
  const usedColorIds = new Set(activeMembers.map((m) => m.avatarColor));

  return `
    <section class="p6-section">
      <h2 class="p6-section-title">アバターカラーパレット</h2>
      <p class="p6-section-desc">メンバー招待時に選択できる色のパレットです。使用中の色は削除できません。</p>

      <div id="p6-color-list" class="p6-color-list">
        ${colors.map((c, idx) => renderColorRow(c, idx, usedColorIds)).join('')}
      </div>

      <div class="p6-section-actions">
        <button type="button" id="p6-add-color-btn" class="btn btn-ghost">+ 色を追加</button>
      </div>
    </section>
  `;
}

function renderColorRow(color, idx, usedColorIds) {
  const usedBy = Array.from(usedColorIds).includes(color.id);
  const usedByMembers = activeMembers
    .filter((m) => m.avatarColor === color.id)
    .map((m) => m.name)
    .join(', ');

  return `
    <div class="p6-color-row" data-color-idx="${idx}">
      <div class="p6-color-preview">
        <span class="p6-color-chip-lg" style="color:${color.text};background:${color.bg}">A</span>
      </div>
      <label class="p6-color-input-group">
        <span class="p6-color-input-label">ID</span>
        <input type="text" data-color-field="id" data-color-idx="${idx}" value="${escapeAttr(color.id)}" maxlength="20" pattern="[a-z0-9_]+">
      </label>
      <label class="p6-color-input-group">
        <span class="p6-color-input-label">ラベル</span>
        <input type="text" data-color-field="label" data-color-idx="${idx}" value="${escapeAttr(color.label)}" maxlength="10">
      </label>
      <label class="p6-color-input-group">
        <span class="p6-color-input-label">文字</span>
        <input type="color" data-color-field="text" data-color-idx="${idx}" value="${escapeAttr(color.text)}">
      </label>
      <label class="p6-color-input-group">
        <span class="p6-color-input-label">背景</span>
        <input type="color" data-color-field="bg" data-color-idx="${idx}" value="${escapeAttr(color.bg)}">
      </label>
      <div class="p6-color-meta">
        ${usedBy
          ? `<span class="p6-color-used" title="${escapeAttr(usedByMembers)}">使用中: ${escapeHtml(usedByMembers)}</span>`
          : ''
        }
      </div>
      <button type="button" class="p6-color-delete" data-color-idx="${idx}" ${usedBy ? 'disabled title="使用中の色は削除できません"' : ''}>削除</button>
    </div>
  `;
}

// ----- セクション D: 休業日カレンダー -----
function renderSectionD() {
  return `
    <section class="p6-section">
      <h2 class="p6-section-title">休業日カレンダー</h2>
      <p class="p6-section-desc">土日・祝日に加えて、年末年始・夏季休暇など独自の休業日を登録します。営業日計算で除外されます。</p>

      <div id="p6-holidays-list" class="p6-holidays-list">
        ${holidays.length === 0
          ? '<div class="p6-empty">休業日は登録されていません</div>'
          : holidays.map(renderHolidayRow).join('')}
      </div>

      <form id="p6-holiday-form" class="p6-holiday-form">
        <input type="text" id="p6-holiday-name" placeholder="名称(例: 年末年始休業)" required maxlength="30">
        <input type="date" id="p6-holiday-start" required>
        <span class="p6-holiday-sep">〜</span>
        <input type="date" id="p6-holiday-end" required>
        <button type="submit" class="btn btn-primary btn-sm">追加</button>
      </form>
    </section>
  `;
}

function renderHolidayRow(h) {
  return `
    <div class="p6-holiday-row" data-holiday-id="${h.id}">
      <div class="p6-holiday-name">${escapeHtml(h.name || '')}</div>
      <div class="p6-holiday-period">${escapeHtml(h.startDate || '')} 〜 ${escapeHtml(h.endDate || '')}</div>
      <button type="button" class="p6-holiday-delete" data-holiday-id="${h.id}">削除</button>
    </div>
  `;
}

// =====================================================================
// イベントバインド
// =====================================================================
function bindSectionEvents() {
  // 期間・組織・アラート閾値: 単純なフィールド変更
  document.querySelectorAll('[data-field]').forEach((input) => {
    input.addEventListener('input', () => {
      const field = input.dataset.field;
      const value = input.type === 'number' ? Number(input.value) : input.value;
      configData[field] = value;
      markDirty();
    });
  });

  // カラーパレット: 各フィールド変更
  document.querySelectorAll('[data-color-field]').forEach((input) => {
    input.addEventListener('input', () => {
      const idx = Number(input.dataset.colorIdx);
      const field = input.dataset.colorField;
      configData.avatarColors[idx][field] = input.value;
      // プレビューも更新
      const row = input.closest('.p6-color-row');
      const chip = row.querySelector('.p6-color-chip-lg');
      const color = configData.avatarColors[idx];
      chip.style.color = color.text;
      chip.style.backgroundColor = color.bg;
      markDirty();
    });
  });

  // カラー追加
  const addBtn = document.getElementById('p6-add-color-btn');
  if (addBtn) {
    addBtn.addEventListener('click', () => {
      const newColor = {
        id: 'new_' + Date.now(),
        label: '新規',
        text: '#475569',
        bg: '#F1F5F9'
      };
      configData.avatarColors.push(newColor);
      markDirty();
      reRenderColorSection();
    });
  }

  // カラー削除
  document.querySelectorAll('.p6-color-delete').forEach((btn) => {
    btn.addEventListener('click', () => {
      if (btn.disabled) return;
      const idx = Number(btn.dataset.colorIdx);
      configData.avatarColors.splice(idx, 1);
      markDirty();
      reRenderColorSection();
    });
  });

  // 休業日追加
  const hForm = document.getElementById('p6-holiday-form');
  if (hForm) {
    hForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      await handleAddHoliday();
    });
  }

  // 休業日削除
  document.querySelectorAll('.p6-holiday-delete').forEach((btn) => {
    btn.addEventListener('click', () => handleDeleteHoliday(btn.dataset.holidayId));
  });
}

function reRenderColorSection() {
  // セクション C だけ再描画
  const content = document.getElementById('p6-content');
  const sections = content.querySelectorAll('.p6-section');
  // 3番目(C)を差し替え
  if (sections[2]) {
    const tmp = document.createElement('div');
    tmp.innerHTML = renderSectionC();
    sections[2].replaceWith(tmp.firstElementChild);
  }
  // 再バインド
  bindSectionEvents();
}

// =====================================================================
// 変更検知
// =====================================================================
function markDirty() {
  document.getElementById('p6-save-btn').disabled = false;
  document.getElementById('p6-dirty-indicator').style.display = 'inline';
  hideMessage();
}

function markClean() {
  document.getElementById('p6-save-btn').disabled = true;
  document.getElementById('p6-dirty-indicator').style.display = 'none';
}

// =====================================================================
// 保存処理(config/system 全体上書き)
// =====================================================================
async function handleSave() {
  // バリデーション
  const errors = validateConfig();
  if (errors.length > 0) {
    showError('入力エラー: ' + errors.join(' / '));
    return;
  }

  // 警告ダイアログ
  if (!window.confirm('現在の設定値を上書き保存します。よろしいですか?\n\n(変更履歴は記録されません)')) {
    return;
  }

  const saveBtn = document.getElementById('p6-save-btn');
  saveBtn.disabled = true;

  try {
    await setDoc(doc(db, 'config', 'system'), {
      ...configData,
      updatedAt: serverTimestamp(),
      updatedBy: auth.currentUser.uid
    });
    markClean();
    showSuccess('保存しました');
  } catch (err) {
    showError('保存に失敗しました: ' + err.message);
    saveBtn.disabled = false;
  }
}

function validateConfig() {
  const errors = [];

  // halfStart < halfEnd
  if (configData.halfStart && configData.halfEnd && configData.halfStart >= configData.halfEnd) {
    errors.push('下半期 開始月は終了月より前である必要があります');
  }

  // fiscalYearStart < fiscalYearEnd
  if (configData.fiscalYearStart && configData.fiscalYearEnd && configData.fiscalYearStart >= configData.fiscalYearEnd) {
    errors.push('期 開始日は終了日より前である必要があります');
  }

  // 色分け閾値: good > 0 > warning > danger
  const g = Number(configData.paceThresholdGood);
  const w = Number(configData.paceThresholdWarning);
  const d = Number(configData.paceThresholdDanger);
  if (!(g > w && w > d)) {
    errors.push('色分け閾値は「順調 > 要注意 > 遅延」の順である必要があります');
  }

  // カラーパレット: id 重複
  const ids = configData.avatarColors.map((c) => c.id);
  const uniqIds = new Set(ids);
  if (ids.length !== uniqIds.size) {
    errors.push('アバターカラーの ID に重複があります');
  }
  // id が空欄
  if (ids.some((id) => !id || !id.trim())) {
    errors.push('アバターカラーの ID は必須です');
  }
  // HEX 形式
  const hexRe = /^#[0-9A-Fa-f]{6}$/;
  configData.avatarColors.forEach((c) => {
    if (!hexRe.test(c.text)) errors.push(`カラー ${c.label || c.id} の文字色が不正`);
    if (!hexRe.test(c.bg)) errors.push(`カラー ${c.label || c.id} の背景色が不正`);
  });

  return errors;
}

// =====================================================================
// 休業日 CRUD
// =====================================================================
async function handleAddHoliday() {
  const name = document.getElementById('p6-holiday-name').value.trim();
  const startDate = document.getElementById('p6-holiday-start').value;
  const endDate = document.getElementById('p6-holiday-end').value;

  if (!name || !startDate || !endDate) {
    showError('休業日: 名称・開始日・終了日を全て入力してください');
    return;
  }
  if (startDate > endDate) {
    showError('休業日: 開始日は終了日以前である必要があります');
    return;
  }

  try {
    await addDoc(collection(db, 'holidays'), {
      name,
      startDate,
      endDate,
      createdAt: serverTimestamp(),
      createdBy: auth.currentUser.uid
    });
    document.getElementById('p6-holiday-form').reset();
    await loadData();
    reRenderHolidaySection();
    showSuccess('休業日を追加しました');
  } catch (err) {
    showError('休業日の追加に失敗しました: ' + err.message);
  }
}

async function handleDeleteHoliday(holidayId) {
  const h = holidays.find((x) => x.id === holidayId);
  if (!h) return;
  if (!window.confirm(`「${h.name}」を削除しますか?`)) return;

  try {
    await deleteDoc(doc(db, 'holidays', holidayId));
    await loadData();
    reRenderHolidaySection();
    showSuccess('休業日を削除しました');
  } catch (err) {
    showError('休業日の削除に失敗しました: ' + err.message);
  }
}

function reRenderHolidaySection() {
  const content = document.getElementById('p6-content');
  const sections = content.querySelectorAll('.p6-section');
  if (sections[3]) {
    const tmp = document.createElement('div');
    tmp.innerHTML = renderSectionD();
    sections[3].replaceWith(tmp.firstElementChild);
  }
  bindSectionEvents();
}

// =====================================================================
// メッセージ
// =====================================================================
function showError(msg) {
  const el = document.getElementById('p6-error');
  el.textContent = msg;
  el.style.display = 'block';
  document.getElementById('p6-success').style.display = 'none';
  setTimeout(() => { el.style.display = 'none'; }, 6000);
}

function showSuccess(msg) {
  const el = document.getElementById('p6-success');
  el.textContent = msg;
  el.style.display = 'block';
  document.getElementById('p6-error').style.display = 'none';
  setTimeout(() => { el.style.display = 'none'; }, 3000);
}

function hideMessage() {
  document.getElementById('p6-error').style.display = 'none';
  document.getElementById('p6-success').style.display = 'none';
}

// =====================================================================
// ユーティリティ
// =====================================================================
function escapeHtml(str) {
  return String(str ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));
}
function escapeAttr(str) {
  return escapeHtml(str);
}
